import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { useT } from '@/i18n/LanguageContext';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const t = useT();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(t.resetPassword.passwordMinLength);
      return;
    }
    if (password !== confirmPassword) {
      setError(t.resetPassword.passwordMismatch);
      return;
    }
    if (!token) {
      setError(t.resetPassword.invalidToken);
      return;
    }

    setLoading(true);
    try {
      await apiFetch('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.resetPassword.failedReset);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout>
        <p className="text-sm text-[#9A9A9A] mb-4">{t.resetPassword.invalidLink}</p>
        <Link to="/forgot-password" className="text-sm text-[#9A9A9A] hover:text-[#523EF7] transition-colors">
          {t.resetPassword.requestNew}
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#523EF7] mb-4">
        {t.common.appName}
      </p>

      <h1 className="text-3xl sm:text-4xl font-bold text-[#222222] tracking-tight leading-tight mb-2">
        {t.resetPassword.title}
      </h1>
      <p className="text-[#9A9A9A] text-base mb-8">
        {success ? t.resetPassword.subtitleSuccess : t.resetPassword.subtitleDefault}
      </p>

      <div className="max-w-sm mx-auto">
        {success ? (
          <div className="text-center space-y-4">
            <div className="w-14 h-14 mx-auto bg-emerald-50 rounded-full flex items-center justify-center">
              <CheckCircle size={24} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#222222]">{t.resetPassword.passwordUpdated}</p>
              <p className="text-sm text-[#9A9A9A] mt-1">
                {t.resetPassword.canSignIn}
              </p>
            </div>
            <Link to="/login" className="inline-block mt-2">
              <button className="auth-btn-primary px-8">{t.resetPassword.goToLogin}</button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 text-left">
            <div className="flex flex-col gap-1">
              <label htmlFor="password" className="text-sm font-medium text-[#3F3F3F]">
                {t.resetPassword.newPassword} <span className="text-red-500">*</span>
              </label>
              <input
                id="password"
                type="password"
                placeholder={t.resetPassword.passwordPlaceholder}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                className="auth-input"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="confirm-password" className="text-sm font-medium text-[#3F3F3F]">
                {t.resetPassword.confirmPassword} <span className="text-red-500">*</span>
              </label>
              <input
                id="confirm-password"
                type="password"
                placeholder={t.resetPassword.confirmPlaceholder}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="auth-input"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="auth-btn-primary w-full"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5 mx-auto" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                t.resetPassword.submit
              )}
            </button>

            <div className="text-center pt-1">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm text-[#9A9A9A] hover:text-[#523EF7] transition-colors"
              >
                <ArrowLeft size={14} />
                {t.common.backToLogin}
              </Link>
            </div>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
